/**
 * Web AuthService implementation — wraps `@aws-amplify/auth` v6.
 *
 * Passkey-first sign-in uses Amplify's `USER_AUTH` flow with
 * `preferredChallenge: 'WEB_AUTHN'`. Adding a new passkey post-login uses
 * `associateWebAuthnCredential`. The implementation hides Amplify's
 * idiosyncratic next-step strings behind the `AuthService` interface so the
 * rest of the app (and a future RN port) speaks a stable vocabulary.
 */

import {
  associateWebAuthnCredential,
  confirmResetPassword,
  confirmSignIn,
  confirmSignUp,
  fetchAuthSession,
  fetchUserAttributes,
  getCurrentUser,
  resendSignUpCode,
  resetPassword,
  signIn,
  signOut,
  signUp,
  updateMFAPreference,
  setUpTOTP,
} from "aws-amplify/auth";

import type {
  AuthService,
  AuthSession,
  SignInResult,
  SignUpResult,
} from "@/core/auth/auth-service";
import { e2eAuthService } from "@/test/e2e-auth-service";

type AmplifySignInStep = string;

const mapSignInNextStep = (
  step: AmplifySignInStep,
  extra: { available?: string[]; setupUri?: string } = {},
): SignInResult["nextStep"] => {
  switch (step) {
    case "DONE":
      return { kind: "DONE" };
    case "CONFIRM_SIGN_UP":
      return { kind: "CONFIRM_SIGN_UP" };
    case "RESET_PASSWORD":
      return { kind: "RESET_PASSWORD" };
    case "CONFIRM_SIGN_IN_WITH_EMAIL_CODE":
      return { kind: "CONFIRM_SIGN_IN_WITH_EMAIL_CODE" };
    case "CONFIRM_SIGN_IN_WITH_TOTP_CODE":
      return { kind: "CONFIRM_SIGN_IN_WITH_TOTP_CODE" };
    case "CONTINUE_SIGN_IN_WITH_FIRST_FACTOR_SELECTION":
      return {
        kind: "CONTINUE_SIGN_IN_WITH_FIRST_FACTOR_SELECTION",
        available: extra.available ?? [],
      };
    case "CONTINUE_SIGN_IN_WITH_TOTP_SETUP":
      return {
        kind: "CONTINUE_SIGN_IN_WITH_TOTP_SETUP",
        setupUri: extra.setupUri ?? "",
      };
    default:
      return { kind: "OTHER", raw: step };
  }
};

const realAmplifyAuthService: AuthService = {
  async currentSession(): Promise<AuthSession | null> {
    try {
      const session = await fetchAuthSession();
      const accessToken = session.tokens?.accessToken;
      const idToken = session.tokens?.idToken;
      if (!accessToken || !idToken) return null;
      const { userId } = await getCurrentUser();
      let email: string | null = null;
      let emailVerified = false;
      try {
        const attrs = await fetchUserAttributes();
        email = attrs.email ?? null;
        emailVerified = attrs.email_verified === "true";
      } catch {
        /* attributes optional */
      }
      return {
        userId,
        email,
        emailVerified,
        accessToken: accessToken.toString(),
        idToken: idToken.toString(),
      };
    } catch {
      return null;
    }
  },

  async getAccessToken(): Promise<string | null> {
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.accessToken;
      return token ? token.toString() : null;
    } catch {
      return null;
    }
  },

  async getIdToken(): Promise<string | null> {
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken;
      return token ? token.toString() : null;
    } catch {
      return null;
    }
  },

  async signIn(input): Promise<SignInResult> {
    const { username, password, preferredChallenge } = input;
    const result = await signIn({
      username,
      ...(password ? { password } : {}),
      options: {
        authFlowType: "USER_AUTH",
        ...(preferredChallenge ? { preferredChallenge } : {}),
      },
    });
    const stepName = result.nextStep.signInStep;
    const available =
      stepName === "CONTINUE_SIGN_IN_WITH_FIRST_FACTOR_SELECTION" &&
      "availableChallenges" in result.nextStep &&
      Array.isArray(result.nextStep.availableChallenges)
        ? (result.nextStep.availableChallenges as string[])
        : undefined;
    const setupUri =
      stepName === "CONTINUE_SIGN_IN_WITH_TOTP_SETUP" &&
      "totpSetupDetails" in result.nextStep &&
      result.nextStep.totpSetupDetails
        ? result.nextStep.totpSetupDetails
            .getSetupUri("HealthAnalyzer")
            .toString()
        : undefined;
    return {
      isSignedIn: result.isSignedIn,
      nextStep: mapSignInNextStep(stepName, {
        ...(available ? { available } : {}),
        ...(setupUri ? { setupUri } : {}),
      }),
    };
  },

  async confirmSignIn(challengeResponse): Promise<SignInResult> {
    const result = await confirmSignIn({ challengeResponse });
    return {
      isSignedIn: result.isSignedIn,
      nextStep: mapSignInNextStep(result.nextStep.signInStep),
    };
  },

  async signUp({ email, password }): Promise<SignUpResult> {
    const result = await signUp({
      username: email,
      password,
      options: { userAttributes: { email } },
    });
    if (result.nextStep.signUpStep === "CONFIRM_SIGN_UP") {
      const dest = result.nextStep.codeDeliveryDetails?.destination ?? null;
      return {
        isSignUpComplete: false,
        nextStep: {
          kind: "CONFIRM_SIGN_UP_WITH_EMAIL_CODE",
          deliveryDestination: dest,
        },
      };
    }
    if (result.nextStep.signUpStep === "DONE") {
      return { isSignUpComplete: true, nextStep: { kind: "DONE" } };
    }
    return {
      isSignUpComplete: false,
      nextStep: { kind: "OTHER", raw: result.nextStep.signUpStep },
    };
  },

  async confirmSignUp({ email, code }): Promise<void> {
    await confirmSignUp({ username: email, confirmationCode: code });
  },

  async resendSignUpCode(email): Promise<void> {
    await resendSignUpCode({ username: email });
  },

  async resetPassword(email): Promise<void> {
    await resetPassword({ username: email });
  },

  async confirmResetPassword({ email, code, newPassword }): Promise<void> {
    await confirmResetPassword({
      username: email,
      confirmationCode: code,
      newPassword,
    });
  },

  async signOut(): Promise<void> {
    await signOut();
  },

  async associatePasskey(): Promise<void> {
    await associateWebAuthnCredential();
  },

  async setMfa(enabled): Promise<{ setupUri: string | null }> {
    if (enabled) {
      const totp = await setUpTOTP();
      await updateMFAPreference({ totp: "PREFERRED" });
      return { setupUri: totp.getSetupUri("HealthAnalyzer").toString() };
    }
    await updateMFAPreference({ totp: "DISABLED" });
    return { setupUri: null };
  },
};

/**
 * E2E swap: Playwright tests run against the dev/preview server with
 * `VITE_E2E=true`, which substitutes a deterministic in-memory `AuthService`
 * so no AWS Cognito, no WebAuthn, and no network is required to drive the
 * auth flows. Production builds always use `realAmplifyAuthService`.
 */
const isE2EMode =
  typeof import.meta !== "undefined" &&
  (import.meta.env as Record<string, string | undefined>).VITE_E2E === "true";

export const amplifyAuthService: AuthService = isE2EMode
  ? e2eAuthService
  : realAmplifyAuthService;
